import { useEffect } from "react";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { Capacitor } from "@capacitor/core";

const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]';

const SKIP_ENTER_NAV_TYPES = new Set([
  "submit",
  "button",
  "checkbox",
  "radio",
  "file",
  "reset",
]);

function findScrollParent(el: HTMLElement): HTMLElement | null {
  const dialog = el.closest('[role="dialog"], [role="alertdialog"]');
  if (dialog) {
    const scrollBody = dialog.querySelector<HTMLElement>(
      ".overflow-y-auto, .native-sheet-compact-body",
    );
    if (scrollBody) return scrollBody;
    return dialog as HTMLElement;
  }

  const authCard = el.closest(".native-auth-card");
  if (authCard instanceof HTMLElement) return authCard;

  const pageMain = el.closest("main.native-auth-route, main.native-layout-main");
  if (pageMain instanceof HTMLElement) {
    const nestedCard = pageMain.querySelector<HTMLElement>(".native-auth-card");
    if (nestedCard) return nestedCard;
  }

  return null;
}

function findFieldScope(el: HTMLElement): HTMLElement | null {
  return (
    el.closest("form") ??
    el.closest(".native-sheet-form") ??
    el.closest('[role="dialog"], [role="alertdialog"]') ??
    el.closest(".native-auth-card")
  );
}

function listFocusableFields(scope: HTMLElement): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => {
      if (node.closest("[hidden], [aria-hidden='true']")) return false;
      if (node.hasAttribute("disabled")) return false;
      const input = node as HTMLInputElement;
      if (node.tagName === "INPUT" && SKIP_ENTER_NAV_TYPES.has(input.type)) {
        return false;
      }
      return node.getClientRects().length > 0;
    },
  );
}

function updateEnterKeyHints(scope: HTMLElement) {
  const fields = listFocusableFields(scope);
  fields.forEach((field, index) => {
    if (field.tagName === "TEXTAREA") return;
    if (field.tagName !== "INPUT") return;
    const input = field as HTMLInputElement;
    const hint = index < fields.length - 1 ? "next" : "done";
    if (input.enterKeyHint !== hint) input.enterKeyHint = hint;
  });
}

function getVisibleBottom(keyboardHeight: number): number {
  const vv = window.visualViewport;
  if (vv) return vv.offsetTop + vv.height;
  if (keyboardHeight > 0) return window.innerHeight - keyboardHeight;
  return window.innerHeight;
}

function clearStuckKeyboardResize() {
  document.documentElement.style.removeProperty("height");
  document.body.style.removeProperty("height");
  document.body.style.removeProperty("min-height");
  window.scrollTo(0, 0);
}

/** Scroll focused fields above the keyboard on native (iOS/Android WebView). */
export function useNativeKeyboardScroll() {
  const { isNative } = useNativePlatform();

  useEffect(() => {
    if (!isNative) return;

    const isIOS = Capacitor.getPlatform() === "ios";
    let keyboardVisible = false;
    let keyboardHeight = 0;
    let lastFocused: HTMLElement | null = null;
    let scrollTimer: number | undefined;

    const setKeyboardOpenClass = (open: boolean) => {
      document.documentElement.classList.toggle(
        "bilenia-native-keyboard-open",
        open,
      );
    };

    const scrollFocusedIntoView = (
      target: EventTarget | null,
      options?: { gentle?: boolean },
    ) => {
      const el = target as HTMLElement | null;
      if (!el?.matches?.(FOCUSABLE)) return;

      const gentle = Boolean(options?.gentle);

      const run = () => {
        const visibleBottom = getVisibleBottom(keyboardHeight) - 16;
        const elRect = el.getBoundingClientRect();
        const container = findScrollParent(el);

        if (container) {
          const boxRect = container.getBoundingClientRect();
          const effectiveBottom = Math.min(boxRect.bottom, visibleBottom);
          const overflow = elRect.bottom - effectiveBottom + 12;
          if (overflow > 0) {
            container.scrollTop += overflow;
            return;
          }
          if (!gentle && elRect.top < boxRect.top + 12) {
            container.scrollTop += elRect.top - boxRect.top - 12;
          }
          return;
        }

        if (elRect.bottom > visibleBottom) {
          try {
            el.scrollIntoView({
              block: "center",
              inline: "nearest",
              behavior: "auto",
            });
          } catch {
            el.scrollIntoView({ block: "nearest", inline: "nearest" });
          }
        }
      };

      if (scrollTimer) window.clearTimeout(scrollTimer);

      window.requestAnimationFrame(run);
      if (gentle) {
        scrollTimer = window.setTimeout(run, isIOS ? 80 : 60);
        return;
      }

      scrollTimer = window.setTimeout(run, isIOS ? 100 : 80);
      window.setTimeout(run, isIOS ? 280 : 220);
    };

    const hideKeyboard = () => {
      void import("@capacitor/keyboard")
        .then(({ Keyboard }) => Keyboard.hide())
        .catch(() => {
          const active = document.activeElement;
          if (active instanceof HTMLElement) active.blur();
        });
    };

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.matches?.(FOCUSABLE)) return;

      setKeyboardOpenClass(true);

      const scope = findFieldScope(target);
      if (scope) updateEnterKeyHints(scope);

      const switchingField =
        keyboardVisible &&
        lastFocused !== null &&
        lastFocused !== target &&
        Boolean(scope) &&
        findFieldScope(lastFocused) === scope;

      lastFocused = target;
      scrollFocusedIntoView(target, { gentle: switchingField });
    };

    const onFocusOut = () => {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (
          active instanceof HTMLElement &&
          active.matches(FOCUSABLE)
        ) {
          if (keyboardVisible) {
            scrollFocusedIntoView(active, { gentle: true });
          }
          return;
        }
        if (!keyboardVisible) {
          setKeyboardOpenClass(false);
        }
      }, isIOS ? 60 : 40);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.defaultPrevented) return;

      const target = e.target as HTMLElement | null;
      if (!target?.matches?.(FOCUSABLE)) return;
      if (target.tagName === "TEXTAREA") return;
      if (target.tagName === "INPUT") {
        const input = target as HTMLInputElement;
        if (SKIP_ENTER_NAV_TYPES.has(input.type)) return;
      }

      const scope = findFieldScope(target);
      if (!scope) return;

      const fields = listFocusableFields(scope);
      const index = fields.indexOf(target);
      if (index < 0) return;

      if (index < fields.length - 1) {
        e.preventDefault();
        fields[index + 1].focus();
        return;
      }

      e.preventDefault();
      hideKeyboard();
    };

    const onKeyboardShow = (info?: { keyboardHeight?: number }) => {
      keyboardHeight = info?.keyboardHeight ?? keyboardHeight;
      const alreadyVisible = keyboardVisible;
      keyboardVisible = true;
      setKeyboardOpenClass(true);

      const active = document.activeElement;
      if (active instanceof HTMLElement && active.matches(FOCUSABLE)) {
        scrollFocusedIntoView(active, { gentle: alreadyVisible });
      }
    };

    const onKeyboardHide = () => {
      keyboardVisible = false;
      keyboardHeight = 0;
      lastFocused = null;
      setKeyboardOpenClass(false);
      window.requestAnimationFrame(clearStuckKeyboardResize);
      window.setTimeout(clearStuckKeyboardResize, isIOS ? 120 : 60);
    };

    const onViewportResize = () => {
      const vv = window.visualViewport;
      const active = document.activeElement;
      if (
        vv &&
        active instanceof HTMLElement &&
        active.matches(FOCUSABLE)
      ) {
        const heightDiff = window.innerHeight - vv.height;
        if (heightDiff > 120) {
          keyboardVisible = true;
          keyboardHeight = heightDiff;
          setKeyboardOpenClass(true);
          scrollFocusedIntoView(active, { gentle: true });
          return;
        }
      }

      if (!keyboardVisible) return;
      if (active instanceof HTMLElement && active.matches(FOCUSABLE)) {
        scrollFocusedIntoView(active, { gentle: true });
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!keyboardVisible) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.matches(FOCUSABLE) || target.closest(FOCUSABLE)) return;
      if (target.closest("button, a, [role='button'], label")) return;

      const active = document.activeElement;
      if (active instanceof HTMLElement && active.matches(FOCUSABLE)) {
        hideKeyboard();
      }
    };

    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    window.visualViewport?.addEventListener("resize", onViewportResize);
    window.visualViewport?.addEventListener("scroll", onViewportResize);

    let removeKeyboard: (() => void) | undefined;
    void import("@capacitor/keyboard")
      .then(async ({ Keyboard }) => {
        const handles = await Promise.all([
          Keyboard.addListener(
            isIOS ? "keyboardWillShow" : "keyboardDidShow",
            onKeyboardShow,
          ),
          Keyboard.addListener(
            isIOS ? "keyboardWillHide" : "keyboardDidHide",
            onKeyboardHide,
          ),
          ...(isIOS
            ? [Keyboard.addListener("keyboardDidHide", onKeyboardHide)]
            : []),
        ]);
        removeKeyboard = () => {
          for (const handle of handles) void handle.remove();
        };
      })
      .catch(() => undefined);

    return () => {
      if (scrollTimer) window.clearTimeout(scrollTimer);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.visualViewport?.removeEventListener("resize", onViewportResize);
      window.visualViewport?.removeEventListener("scroll", onViewportResize);
      setKeyboardOpenClass(false);
      removeKeyboard?.();
    };
  }, [isNative]);
}
